// Content Randomizer - Live content mutation for unreliable narrator effect
// Items randomly swap to different variants while the user is viewing the page.

import contentPools from '../content/book-variants.json';
import { glitchText } from './effects';
import { prefersReducedMotion } from './animations';

// Type definitions
interface Review {
  quote: string;
  source: string;
  author?: string;
}

interface AuthorFact {
  id: string;
  text: string;
  type: 'normal' | 'italic' | 'redacted' | 'glitch';
  revealText?: string;
}

interface GuestbookMessage {
  id: string;
  timestamp: string;
  author: string;
  content: string;
  corrupted?: boolean;
}

interface ContentPools {
  reviews: Review[];
  authorFacts: AuthorFact[];
  guestbookMessages: GuestbookMessage[];
}

// Configuration for mutation timing
const MUTATION_CONFIG = {
  // Minimum time before first mutation (ms)
  minInitialDelay: 8000,
  // Maximum time before first mutation (ms)
  maxInitialDelay: 20000,
  // Minimum time between mutations (ms)
  minInterval: 12000,
  // Maximum time between mutations (ms)
  maxInterval: 45000,
  // Probability that any given item will mutate when its turn comes
  mutationProbability: 0.4,
  // Duration of glitch effect during swap (ms)
  glitchDuration: 400
};

// Track active mutation timers for cleanup
const activeMutations = new Map<HTMLElement, ReturnType<typeof setTimeout>>();

/**
 * Get a random item from an array
 */
function getRandomItem<T>(array: T[]): T {
  return array[Math.floor(Math.random() * array.length)];
}

/**
 * Get a random item from an array that's different from the current one
 */
function getRandomDifferentItem<T>(array: T[], currentIndex: number): { item: T; index: number } {
  if (array.length <= 1) return { item: array[0], index: 0 };

  let newIndex: number;
  do {
    newIndex = Math.floor(Math.random() * array.length);
  } while (newIndex === currentIndex && array.length > 1);

  return { item: array[newIndex], index: newIndex };
}

/**
 * Get random delay within configured range
 */
function getRandomDelay(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

/**
 * Get initial reviews - randomly selected subset from the pool
 */
export function getInitialReviews(count: number = 7): Review[] {
  const pool = (contentPools as ContentPools).reviews;
  const shuffled = [...pool].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, Math.min(count, pool.length));
}

/**
 * Get initial author facts - one from each category
 * Categories are determined by ID prefix (opening, disclaimer, redacted-1, etc.)
 */
export function getInitialAuthorFacts(): AuthorFact[] {
  const pool = (contentPools as ContentPools).authorFacts;

  // Group by category (ID prefix before the number)
  const categories = new Map<string, AuthorFact[]>();
  pool.forEach(fact => {
    const category = fact.id.replace(/-\d+[a-z]?$/, '');
    if (!categories.has(category)) {
      categories.set(category, []);
    }
    categories.get(category)!.push(fact);
  });

  // Pick one random fact from each category, in order
  const categoryOrder = [
    'opening', 'disclaimer', 'redacted-1', 'origins', 'education',
    'previous-works', 'redacted-2', 'influences', 'location', 'redacted-3', 'contact'
  ];

  const result: AuthorFact[] = [];
  categoryOrder.forEach(cat => {
    const items = categories.get(cat);
    if (items && items.length > 0) {
      result.push(getRandomItem(items));
    }
  });

  return result;
}

/**
 * Get initial guestbook messages - randomly selected subset
 */
export function getInitialGuestbookMessages(count: number = 12): GuestbookMessage[] {
  const pool = (contentPools as ContentPools).guestbookMessages;
  const shuffled = [...pool].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, Math.min(count, pool.length));
}

/**
 * Get all reviews from the pool (for mutation)
 */
export function getAllReviews(): Review[] {
  return (contentPools as ContentPools).reviews;
}

/**
 * Get author facts by category (for mutation)
 */
export function getAuthorFactsByCategory(factId: string): AuthorFact[] {
  const pool = (contentPools as ContentPools).authorFacts;
  const category = factId.replace(/-\d+[a-z]?$/, '');
  return pool.filter(fact => fact.id.startsWith(category));
}

/**
 * Get all guestbook messages from the pool (for mutation)
 */
export function getAllGuestbookMessages(): GuestbookMessage[] {
  return (contentPools as ContentPools).guestbookMessages;
}

/**
 * Setup live mutation for a review element
 * @param element The DOM element containing the review
 * @param currentIndex Current index in the pool
 * @param onMutate Callback when content changes, receives new review
 * @returns Cleanup function
 */
export function setupReviewMutation(
  element: HTMLElement,
  currentIndex: number,
  onMutate: (review: Review, newIndex: number) => void
): () => void {
  if (prefersReducedMotion()) {
    return () => {};
  }

  const pool = getAllReviews();
  let currentIdx = currentIndex;

  const scheduleMutation = () => {
    const delay = getRandomDelay(MUTATION_CONFIG.minInterval, MUTATION_CONFIG.maxInterval);

    const timeoutId = setTimeout(() => {
      // Random chance to actually mutate
      if (Math.random() < MUTATION_CONFIG.mutationProbability) {
        const { item: newReview, index: newIndex } = getRandomDifferentItem(pool, currentIdx);
        currentIdx = newIndex;

        // Apply glitch effect then update
        const quoteEl = element.querySelector('.press-quote-text');
        if (quoteEl) {
          glitchText(quoteEl as HTMLElement, MUTATION_CONFIG.glitchDuration);
        }

        setTimeout(() => {
          onMutate(newReview, newIndex);
        }, MUTATION_CONFIG.glitchDuration / 2);
      }

      // Schedule next mutation
      scheduleMutation();
    }, delay);

    activeMutations.set(element, timeoutId);
  };

  // Initial delay before first possible mutation
  const initialDelay = getRandomDelay(MUTATION_CONFIG.minInitialDelay, MUTATION_CONFIG.maxInitialDelay);
  const initialTimeoutId = setTimeout(scheduleMutation, initialDelay);
  activeMutations.set(element, initialTimeoutId);

  return () => {
    const timeoutId = activeMutations.get(element);
    if (timeoutId) {
      clearTimeout(timeoutId);
      activeMutations.delete(element);
    }
  };
}

/**
 * Setup live mutation for an author fact element
 * @param element The DOM element containing the fact
 * @param factId The ID of the current fact (used to determine category)
 * @param onMutate Callback when content changes
 * @returns Cleanup function
 */
export function setupAuthorFactMutation(
  element: HTMLElement,
  factId: string,
  onMutate: (fact: AuthorFact) => void
): () => void {
  if (prefersReducedMotion()) {
    return () => {};
  }

  const pool = getAuthorFactsByCategory(factId);
  if (pool.length <= 1) {
    return () => {};
  }

  let currentIdx = pool.findIndex(f => f.id === factId);
  if (currentIdx === -1) currentIdx = 0;

  const scheduleMutation = () => {
    const delay = getRandomDelay(MUTATION_CONFIG.minInterval, MUTATION_CONFIG.maxInterval);

    const timeoutId = setTimeout(() => {
      if (Math.random() < MUTATION_CONFIG.mutationProbability) {
        const { item: newFact, index: newIndex } = getRandomDifferentItem(pool, currentIdx);
        currentIdx = newIndex;

        // Apply glitch effect
        glitchText(element, MUTATION_CONFIG.glitchDuration);

        setTimeout(() => {
          onMutate(newFact);
        }, MUTATION_CONFIG.glitchDuration / 2);
      }

      scheduleMutation();
    }, delay);

    activeMutations.set(element, timeoutId);
  };

  const initialDelay = getRandomDelay(MUTATION_CONFIG.minInitialDelay, MUTATION_CONFIG.maxInitialDelay);
  const initialTimeoutId = setTimeout(scheduleMutation, initialDelay);
  activeMutations.set(element, initialTimeoutId);

  return () => {
    const timeoutId = activeMutations.get(element);
    if (timeoutId) {
      clearTimeout(timeoutId);
      activeMutations.delete(element);
    }
  };
}

/**
 * Setup live mutation for a guestbook message element
 * @param element The DOM element containing the message
 * @param currentIndex Current index in the displayed messages
 * @param onMutate Callback when content changes
 * @returns Cleanup function
 */
export function setupGuestbookMutation(
  element: HTMLElement,
  currentIndex: number,
  onMutate: (message: GuestbookMessage, newIndex: number) => void
): () => void {
  if (prefersReducedMotion()) {
    return () => {};
  }

  const pool = getAllGuestbookMessages();
  let currentIdx = currentIndex;

  const scheduleMutation = () => {
    const delay = getRandomDelay(MUTATION_CONFIG.minInterval, MUTATION_CONFIG.maxInterval);

    const timeoutId = setTimeout(() => {
      if (Math.random() < MUTATION_CONFIG.mutationProbability) {
        const { item: newMessage, index: newIndex } = getRandomDifferentItem(pool, currentIdx);
        currentIdx = newIndex;

        // Apply glitch to content area
        const contentEl = element.querySelector('.guestbook-message-content');
        if (contentEl) {
          glitchText(contentEl as HTMLElement, MUTATION_CONFIG.glitchDuration);
        }

        setTimeout(() => {
          onMutate(newMessage, newIndex);
        }, MUTATION_CONFIG.glitchDuration / 2);
      }

      scheduleMutation();
    }, delay);

    activeMutations.set(element, timeoutId);
  };

  const initialDelay = getRandomDelay(MUTATION_CONFIG.minInitialDelay, MUTATION_CONFIG.maxInitialDelay);
  const initialTimeoutId = setTimeout(scheduleMutation, initialDelay);
  activeMutations.set(element, initialTimeoutId);

  return () => {
    const timeoutId = activeMutations.get(element);
    if (timeoutId) {
      clearTimeout(timeoutId);
      activeMutations.delete(element);
    }
  };
}

/**
 * Stop all active mutations (useful for page cleanup)
 */
export function stopAllMutations(): void {
  activeMutations.forEach((timeoutId) => {
    clearTimeout(timeoutId);
  });
  activeMutations.clear();
}
